import { ChevronRight, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCatalogProducts, getFeaturedProducts, getShelves, shelfForLink, type Product, type Shelf } from './components/Product/productData';
import ProductCatalog from './components/ProductCatalog/ProductCatalog';
import { useShell } from './components/Shell/ShellContext';
import { ToolPage } from './components/ToolPage/ToolPage';
import { readRecent } from './components/ToolsSwitcher/recentTools';

const SHELF_TILES = 4;
const TOP_ROW_TILES = 4;

const TILE_CLASS =
  'flex min-w-0 flex-col items-center gap-1 rounded-xl px-0.5 py-1.5 text-center text-xs font-medium leading-[1.18] text-white no-underline transition-colors hover:bg-white/[0.07] hover:no-underline active:bg-white/10 sm:flex-row sm:gap-2.5 sm:px-2.5 sm:text-left sm:text-[0.9375rem]';

function ToolTile({ product }: { product: Product }) {
  return (
    <Link to={product.to} title={product.summary} className={`${TILE_CLASS} min-h-[68px] sm:min-h-14`}>
      <span aria-hidden className="select-none text-[1.4rem] leading-none sm:w-8 sm:shrink-0 sm:text-center sm:text-[1.6rem]">
        {product.icon}
      </span>
      <span className="min-w-0">{product.primary}</span>
    </Link>
  );
}

function ShelfCard({ shelf }: { shelf: Shelf }) {
  const overflow = shelf.products.length > SHELF_TILES;
  const shown = overflow ? shelf.products.slice(0, SHELF_TILES - 1) : shelf.products;
  const rest = overflow ? shelf.products.slice(SHELF_TILES - 1) : [];
  const shelfLink = `/?shelf=${shelf.id}`;

  return (
    <section aria-label={shelf.name} className="min-w-0 rounded-2xl bg-[#0f0f0f] p-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] sm:p-2.5">
      <h2 className="m-0 px-2 pt-1 sm:px-2.5">
        <Link
          to={shelfLink}
          className="label-mono relative inline-flex min-h-6 items-center gap-1 no-underline after:absolute after:-top-2.5 after:bottom-0 after:inset-x-0 hover:text-white hover:no-underline"
        >
          {shelf.name}
          <ChevronRight aria-hidden className="size-3" />
        </Link>
      </h2>
      <div className="grid grid-cols-2 gap-0.5">
        {shown.map((product) => (
          <ToolTile key={product.to} product={product} />
        ))}
        {overflow ? (
          // The small cluster comes from the App Library: it stands for the rest of the shelf.
          <Link
            to={shelfLink}
            aria-label={`Show ${rest.length} more ${shelf.name} tools`}
            className={`${TILE_CLASS} min-h-[68px] sm:min-h-14`}
          >
            <span aria-hidden className="grid select-none grid-cols-2 gap-px text-[0.6875rem] leading-3 sm:w-8 sm:shrink-0">
              {rest.slice(0, 4).map((product) => (
                <span key={product.to} className="text-center">
                  {product.icon}
                </span>
              ))}
            </span>
            <span className="min-w-0">{rest.length} More</span>
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function TopRow() {
  const catalog = getCatalogProducts();
  const recent = readRecent()
    .map((path) => catalog.find(({ to }) => to === path))
    .filter((product): product is Product => Boolean(product))
    .slice(0, TOP_ROW_TILES);
  const products = recent.length > 0 ? recent : getFeaturedProducts(catalog);

  return (
    <section aria-label={recent.length > 0 ? 'Recent' : 'Start Here'}>
      <h2 className="label-mono m-0 mb-1.5 pl-0.5">{recent.length > 0 ? 'Recent' : 'Start Here'}</h2>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {products.map((product) => (
          <Link
            key={product.to}
            to={product.to}
            className={`${TILE_CLASS} min-h-[68px] sm:min-h-[60px] sm:bg-[#111] sm:px-3 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)] sm:hover:bg-[#1a1a1a]`}
          >
            <span aria-hidden className="select-none text-[1.4rem] leading-none sm:w-8 sm:shrink-0 sm:text-center sm:text-[1.6rem]">
              {product.icon}
            </span>
            <span className="min-w-0">
              <span className="block">{product.primary}</span>
              <span className="hidden truncate text-[0.8125rem] font-normal leading-snug text-muted-foreground lg:block">{product.summary}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function App() {
  const [params] = useSearchParams();
  const { openTools } = useShell();
  const shelf = shelfForLink(params);

  if (shelf) {
    return (
      <ToolPage title={shelf.name} description={`${shelf.products.length} tools`} contentMaxWidth={720}>
        <ProductCatalog products={shelf.products} />
      </ToolPage>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-1 flex-col gap-3 px-4 pb-16 pt-3 sm:gap-6 sm:px-6 lg:pt-8">
      <header>
        <h1 className="font-display text-balance text-[clamp(1.875rem,5vw,2.75rem)] font-extrabold tracking-tight text-foreground">
          Hi there!
        </h1>
        <p className="mt-1 max-w-[60ch] text-pretty text-[0.9375rem] leading-snug text-muted-foreground sm:mt-1.5 sm:text-lg">
          I&apos;m Saarim, and I love building amazing things.{' '}
          <a href="https://saarim.me" target="_blank" rel="noopener noreferrer" className="whitespace-nowrap font-medium text-primary underline-offset-4 hover:underline">
            About me
          </a>
        </p>
      </header>

      <button
        type="button"
        onClick={openTools}
        className="flex h-[46px] w-full shrink-0 items-center gap-2 rounded-xl bg-[#141414] px-3 text-left text-base text-white/60 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] md:hidden"
      >
        <Search aria-hidden className="size-5 shrink-0" />
        Search {getCatalogProducts().length} tools
      </button>

      <TopRow />

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-3 lg:gap-3.5">
        {getShelves().map((item) => (
          <ShelfCard key={item.id} shelf={item} />
        ))}
      </div>
    </div>
  );
}

export default App;
